import { IPersonaStyles } from '@fluentui/react';
import React, { FunctionComponent } from 'react';

import AppContext from 'App/AppContext';
import { IPIconButton, IPScrollablePane, IPSpinner } from 'baseComponents';
import { IPList } from 'baseComponents/List/IPList';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { SkypeCircleCheckIcon } from 'components/Checklist/CheckListStyles';
import HrefLink from 'components/Links/HrefLink';
import SingleTask from 'components/Tasks/SingleTask';
import DateDividerListItem, {
  DataGroup,
  DateComparer,
  DateDivider,
  insertDividersAndGroupsInList,
} from 'components/Utils/ListItemDateDividers';
import Task from 'models/tasks/task';
import { useTranslation } from 'react-i18next';
import { toLocaleDateTimeMedium } from 'utils/datetime';
import { sortOnDate } from 'utils/sorting';
import { toNameInitial } from 'utils/string';
import { IWidgetRendererProps } from '../WidgetRenderer';

interface IWidgetMyCurrentTasksProps extends IWidgetRendererProps {}

const WidgetMyCurrentTasks: FunctionComponent<IWidgetMyCurrentTasksProps> = (props: IWidgetMyCurrentTasksProps) => {
  const { t } = useTranslation(['translation', 'widgets']);
  const appContext = React.useContext(AppContext);
  const [tasks, setTasks] = React.useState<Task[]>([]);
  const [tasksDisplay, setTasksDisplay] = React.useState<(Task | DateDivider | DataGroup<Task>)[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [selectedTask, setSelectedTask] = React.useState<Task | undefined>(undefined);

  React.useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget, props.sharedData]);

  React.useEffect(() => {
    const getCompareDate: DateComparer<Task> = (a) => {
      return a.startDateTime;
    };

    if (tasks && tasks?.length > 0) {
      const newData = insertDividersAndGroupsInList(tasks, getCompareDate, undefined, undefined);
      setTasksDisplay(newData);
    }
  }, [appContext.globalDataCache, tasks]);

  const loadData = async (refresh?: boolean) => {
    try {
      if (props.onRefreshSharedData && props.widget.dashboard && refresh) {
        await props.onRefreshSharedData();

        return;
      }

      if (isLoading) return;
      setIsLoading(true);

      const taskCol = props.sharedData?.myTasks ?? [];

      //show tasks that are not completed, have started, and are not overdue
      const now = new Date();
      const filteredTasks = taskCol
        .filter(
          (t) =>
            t.completed === undefined &&
            t.startDateTime <= now &&
            t.getMinutesOverDeadline(now, appContext.globalDataCache) < 0,
        )
        .sort((a, b) => sortOnDate(a.startDateTime, b.startDateTime));

      setTasks(filteredTasks);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const personaStyle: Partial<IPersonaStyles> = {
    root: {
      marginBottom: 5,
      selectors: {
        '&:hover': {
          cursor: 'pointer',
        },
      },
    },
  };

  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  //dismissing the modal is close's job: SingleTask also saves WITHOUT closing (a task type pane that
  //needs the stored task first), and clearing the selection here unmounts it in the middle of that
  const saveTask = async (task: Task, isNew: boolean, isCancelled: boolean) => {
    if (!isCancelled) {
      await loadData(true);
    }
  };

  const removeTask = async (task: Task) => {
    setSelectedTask(undefined);
    await loadData(true);
  };

  const updateTask = async (task: Task) => {};

  const getMyTasksLink = () => {
    return (
      <IPStack.Item>
        <HrefLink href="/tasks/mytasks">{t('widgets:MyCurrentTasks.MyTasks')}</HrefLink>
      </IPStack.Item>
    );
  };

  return (
    <IPStack verticalFill styles={{ root: { paddingTop: 5 } }}>
      <IPStack.Item grow styles={{ root: { position: 'relative' } }}>
        <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
          <IPList
            items={tasksDisplay}
            onRenderCell={(task) => {
              if (!task) return null;

              if (task instanceof DateDivider) {
                return <DateDividerListItem {...task} />;
              }

              if (task instanceof DataGroup) {
                return null;
              }

              return (
                <IPPersona
                  size={IPPersonaSize.size40}
                  text={task.name}
                  imageInitials={toNameInitial(appContext.globalDataCache.users.get(task.userId).name)}
                  secondaryText={toLocaleDateTimeMedium(task.getDeadline(appContext.globalDataCache))}
                  onClick={() => {
                    if (!task) return;

                    setSelectedTask(task);
                  }}
                  styles={personaStyle}
                />
              );
            }}
          />
        </IPScrollablePane>
      </IPStack.Item>
      {tasks?.length === 0 && (
        <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
          <IPStack grow horizontalAlign="center" verticalAlign="center">
            <IPIconButton iconProps={SkypeCircleCheckIcon} />
          </IPStack>
          {getMyTasksLink()}
        </IPStack>
      )}
      {tasks?.length > 0 && (
        <IPStack horizontalAlign="center" verticalAlign="center">
          {getMyTasksLink()}
        </IPStack>
      )}
      {selectedTask && (
        <SingleTask
          task={selectedTask}
          isOpen={true}
          close={() => {
            setSelectedTask(undefined);
          }}
          onSave={saveTask}
          onRemove={removeTask}
          onUpdate={updateTask}
        />
      )}
    </IPStack>
  );
};

export default WidgetMyCurrentTasks;
